// Lógica pura da importação de fatura de cartão (sem React): ler o CSV do banco,
// sugerir quais colunas são data/descrição/valor e montar as compras para enviar à API.
import { normalizeText, parseCsvLine, parseDate, parseValor } from './csvUtils';

const limpa = (c) => (c || '').replace(/^"|"$/g, '').trim();

/**
 * Escolhe o separador testando cada um nas primeiras linhas: vence o que dá o mesmo número de
 * colunas (>1) em mais linhas e, em empate, o que dá mais colunas. Evita confundir a vírgula
 * decimal (45,90) com separador em arquivos sem cabeçalho.
 */
function descobrirSeparador(linhas) {
  const amostra = linhas.slice(0, 6);
  let melhor = { c: ';', consistentes: -1, colunas: 0 };
  [';', '\t', ','].forEach((c) => {
    const n = amostra.map((l) => parseCsvLine(l, c).length);
    const alvo = n[0];
    const consistentes = alvo > 1 ? n.filter((x) => x === alvo).length : 0;
    if (consistentes > melhor.consistentes || (consistentes === melhor.consistentes && alvo > melhor.colunas)) {
      melhor = { c, consistentes, colunas: alvo };
    }
  });
  return melhor.c;
}

/** Lê o texto do CSV em uma matriz de células, descobrindo o separador (; , ou tab). */
export function lerTabela(texto) {
  const linhas = (texto || '').split(/\r?\n/).filter((l) => l.trim());
  if (!linhas.length) return { delimitador: ';', linhas: [] };
  const delimitador = descobrirSeparador(linhas);
  return { delimitador, linhas: linhas.map((l) => parseCsvLine(l, delimitador).map(limpa)) };
}

/** A primeira linha é cabeçalho quando nenhuma célula parece data nem valor numérico. */
export function temCabecalho(primeiraLinha) {
  return !(primeiraLinha || []).some((c) => parseDate(c) || (/\d/.test(c) && !Number.isNaN(parseValor(c))));
}

const NOMES = {
  data: ['data', 'date', 'dt'],
  descricao: ['descricao', 'lancamento', 'estabelecimento', 'historico', 'title', 'titulo', 'description', 'detalhe', 'detalhes'],
  valor: ['valor', 'amount', 'value', 'quantia'],
  categoria: ['categoria', 'category'],
};

/** Sugere o índice de cada coluna; -1 quando não achou. Usa o nome do cabeçalho ou, sem ele, o conteúdo. */
export function sugerirMapeamento(cabecalho, amostra) {
  const m = { data: -1, descricao: -1, valor: -1, categoria: -1 };
  const nomes = (cabecalho || []).map(normalizeText);
  Object.keys(NOMES).forEach((campo) => {
    // nome exato vence "começa com": entre "Valor Moeda Local", "Valor em Dólar" e "Valor", o certo é o último
    let i = nomes.findIndex((n) => NOMES[campo].includes(n));
    if (i < 0) i = nomes.findIndex((n) => NOMES[campo].some((k) => n.startsWith(k)));
    if (i >= 0) m[campo] = i;
  });
  const ncols = Math.max(...[cabecalho || [], ...(amostra || [])].map((l) => l.length), 0);
  const col = (i) => (amostra || []).map((l) => l[i] || '').filter(Boolean);
  const maioria = (cells, teste) => cells.length > 0 && cells.filter(teste).length / cells.length >= 0.6;
  for (let i = 0; i < ncols; i += 1) {
    const cells = col(i);
    if (m.data < 0 && maioria(cells, (c) => !!parseDate(c))) m.data = i;
  }
  for (let i = ncols - 1; i >= 0; i -= 1) {
    const cells = col(i);
    if (m.valor < 0 && i !== m.data && maioria(cells, (c) => /\d/.test(c) && !Number.isNaN(parseValor(c)))) m.valor = i;
  }
  if (m.descricao < 0) {
    let melhor = -1;
    let tam = 0;
    for (let i = 0; i < ncols; i += 1) {
      if (i === m.data || i === m.valor || i === m.categoria) continue;
      const media = col(i).reduce((s, c) => s + c.length, 0) / Math.max(col(i).length, 1);
      if (media > tam) { tam = media; melhor = i; }
    }
    m.descricao = melhor;
  }
  return m;
}

/** Quando a maioria dos valores é negativa, as compras são os valores negativos (ex.: alguns bancos). */
export function sugerirSinal(linhas, indiceValor) {
  if (indiceValor < 0) return 'positivo';
  const v = linhas.map((l) => parseValor(l[indiceValor])).filter((n) => !Number.isNaN(n) && n !== 0);
  return v.filter((n) => n < 0).length > v.filter((n) => n > 0).length ? 'negativo' : 'positivo';
}

/** "Parcela 2/10", "PARC 02/10", "(2/10)" ou "2 de 10" → { atual, total }. Datas soltas (05/09) não contam. */
export function extrairParcela(descricao) {
  const t = descricao || '';
  const m = t.match(/parc(?:ela)?\.?\s*(\d{1,2})\s*(?:\/|de)\s*(\d{1,2})/i)
    || t.match(/\((\d{1,2})\s*\/\s*(\d{1,2})\)/)
    || t.match(/\b(\d{1,2})\s+de\s+(\d{1,2})\b/i)
    || t.match(/\S\s{2,}(\d{1,2})\/(\d{1,2})\s*$/);   // bancos que preenchem a descrição com espaços: "DROGASIL1676      2/3"
  if (!m) return null;
  const atual = Number(m[1]);
  const total = Number(m[2]);
  return total >= 2 && total <= 120 && atual >= 1 && atual <= total ? { atual, total } : null;
}

// Linhas que não são compras: pagamento da fatura anterior, saldo, totais
const NAO_E_COMPRA = /pagamento (recebido|da fatura|de fatura|efetuado|on[- ]?line)|saldo (anterior|restante)|total da fatura|credito de/i;

/**
 * Converte as linhas do CSV em compras. `sinal`: 'positivo' (compras são valores > 0) ou 'negativo'.
 * Retorna { itens, ignoradas, erros }; `linha` é o número da linha no arquivo (1 = primeira).
 */
export function montarItens(linhas, mapa, { cabecalho = true, sinal = 'positivo', categorias = [] } = {}) {
  const itens = [];
  const ignoradas = [];
  const erros = [];
  const porNome = new Map(categorias.filter((c) => c.tipo === 'DESPESA').map((c) => [normalizeText(c.nome), c.id]));

  linhas.forEach((l, idx) => {
    const linha = idx + 1 + (cabecalho ? 1 : 0);
    if (!l.some(Boolean)) return;
    const descricao = (l[mapa.descricao] || '').replace(/\s+/g, ' ').trim();
    const bruto = parseValor(l[mapa.valor]);
    const data = parseDate(l[mapa.data]);

    if (!data) { erros.push({ linha, erro: 'Data inválida' }); return; }
    if (!descricao) { erros.push({ linha, erro: 'Descrição vazia' }); return; }
    if (Number.isNaN(bruto)) { erros.push({ linha, erro: 'Valor inválido' }); return; }

    const valor = sinal === 'negativo' ? -bruto : bruto;
    if (valor === 0) { ignoradas.push({ linha, descricao, motivo: 'valor zero' }); return; }
    if (valor < 0) { ignoradas.push({ linha, descricao, motivo: 'estorno ou crédito' }); return; }
    if (NAO_E_COMPRA.test(descricao)) { ignoradas.push({ linha, descricao, motivo: 'pagamento/saldo da fatura' }); return; }

    const parcela = extrairParcela((l[mapa.descricao] || '').trim());   // com os espaços originais (alguns bancos separam "2/3" por espaços)
    itens.push({
      linha,
      data,
      descricao,
      valor: Math.round(valor * 100) / 100,
      ...(parcela ? { parcelaAtual: parcela.atual, parcelaTotal: parcela.total } : {}),
      ...(mapa.categoria >= 0 && porNome.has(normalizeText(l[mapa.categoria])) ? { categoriaId: porNome.get(normalizeText(l[mapa.categoria])) } : {}),
    });
  });
  return { itens, ignoradas, erros };
}

/** Chave do "layout" do arquivo (cabeçalhos): o mapeamento só é reaproveitado para o mesmo layout. */
export const chaveLayout = (cabecalho) => (cabecalho || []).map(normalizeText).join('|');

const LS = (cartaoId) => `gastify:fatura-mapa:${cartaoId}`;

export function lembrarMapeamento(cartaoId, cabecalho, mapa, sinal) {
  try { localStorage.setItem(LS(cartaoId), JSON.stringify({ layout: chaveLayout(cabecalho), mapa, sinal })); } catch { /* sem storage */ }
}

export function recuperarMapeamento(cartaoId, cabecalho) {
  try {
    const salvo = JSON.parse(localStorage.getItem(LS(cartaoId)) || 'null');
    return salvo && salvo.layout && salvo.layout === chaveLayout(cabecalho) ? salvo : null;
  } catch { return null; }
}
